import { api } from './apiClient'

/**
 * Authentication API module
 * Handles all authentication-related API calls
 */
export const authApi = {
  /**
   * Send admin login request - validates user, sends OTP, and returns user details
   * @param {string} phoneNumber - Phone number in format 9876543210
   * @returns {Promise<{success: boolean, role?: string, uid?: string, userData?: object, companyId?: string, companyData?: object, dealerships?: array, dealershipsCount?: number, message?: string}>}
   */
  adminLogin: async (phoneNumber) => {
    try {
      const response = await api.get('/auth/adminLogin', {
        phoneNumber: phoneNumber
      })
      return response
    } catch (error) {
      // Temporary fallback for testing - remove when API is ready
      if (error.code === 'ECONNREFUSED' || error.response?.status === 404) {
        console.warn('API endpoint not available, using mock response for testing')
        
        // Mock different responses based on phone number for testing
        const lastDigit = phoneNumber.slice(-1)
        
        if (lastDigit === '1') {
          // Mock admin user
          return {
            success: true,
            role: 'admin',
            uid: 'mock-firebase-uid-admin',
            userData: {
              name: 'Test Admin',
              phoneNumber: phoneNumber,
              email: 'admin@test.com'
            },
            message: 'OTP sent successfully (mock)'
          }
        } else if (lastDigit === '2') {
          // Mock dealer with single dealership
          return {
            success: true,
            role: 'dealer',
            uid: 'mock-firebase-uid-dealer-single',
            userData: {
              name: 'Test Dealer Single',
              phoneNumber: phoneNumber,
              email: 'dealer@test.com'
            },
            companyId: 'company1',
            companyData: {
              name: 'Single Dealership Company',
              address: 'Single Address'
            },
            message: 'OTP sent successfully (mock)'
          }
        } else if (lastDigit === '3') {
          // Mock dealer with multiple dealerships
          return {
            success: true,
            role: 'dealer',
            uid: 'mock-firebase-uid-dealer-multi',
            userData: {
              name: 'Test Dealer Multi',
              phoneNumber: phoneNumber,
              email: 'dealer@test.com'
            },
            companyId: 'company2',
            companyData: {
              name: 'Multi Dealership Company',
              address: 'Multi Address'
            },
            dealerships: [
              { id: 'dealer1', name: 'Dealership 1', address: 'Address 1' },
              { id: 'dealer2', name: 'Dealership 2', address: 'Address 2' },
              { id: 'dealer3', name: 'Dealership 3', address: 'Address 3' }
            ],
            dealershipsCount: 3,
            message: 'OTP sent successfully (mock)'
          }
        } else {
          // Mock member with multiple dealerships
          return {
            success: true,
            role: 'member',
            uid: 'mock-firebase-uid-member',
            userData: {
              name: 'Test Member',
              phoneNumber: phoneNumber,
              email: 'member@test.com'
            },
            dealerships: [
              { id: 'dealer1', name: 'Dealership 1', address: 'Address 1' },
              { id: 'dealer2', name: 'Dealership 2', address: 'Address 2' }
            ],
            dealershipsCount: 2,
            message: 'OTP sent successfully (mock)'
          }
        }
      }
      throw error
    }
  },


  /**
   * Resend OTP to phone number
   * @param {string} phoneNumber - Phone number in format 9876543210
   * @returns {Promise<{success: boolean, message?: string}>}
   */
  resendOtp: async (phoneNumber) => {
    try {
      const response = await api.get('/auth/resendOtp', {
        phoneNumber: phoneNumber
      })
      return response
    } catch (error) {
      throw error
    }
  },

  /**
   * Verify OTP and get Firebase custom token
   * @param {string} phoneNumber - Phone number in format 9876543210
   * @param {string} otp - 6-digit OTP
   * @returns {Promise<{success: boolean, phoneNumber: string, uid: string, customToken: string, verified: boolean}>}
   */
  verifyOtp: async (phoneNumber, otp) => {
    try {
      const response = await api.get('/auth/verifyOtp', {
        phoneNumber: phoneNumber,
        otp: otp
      })
      return response
    } catch (error) {
      // Temporary fallback for testing - remove when API is ready
      if (error.code === 'ECONNREFUSED' || error.response?.status === 404) {
        console.warn('API endpoint not available, using mock response for testing')
        return {
          success: true,
          phoneNumber: phoneNumber,
          uid: 'mock-firebase-uid-123',
          customToken: 'mock-firebase-custom-token-123',
          verified: true
        }
      }
      throw error
    }
  },

  /**
   * Refresh authentication token
   * @returns {Promise<{success: boolean, token?: string}>}
   */
  refreshToken: async () => {
    try {
      const response = await api.post('/auth/refresh')
      return response
    } catch (error) {
      throw error
    }
  },

  /**
   * Logout user
   * @returns {Promise<{success: boolean}>}
   */
  logout: async () => {
    try {
      const response = await api.post('/auth/logout')
      return response
    } catch (error) {
      throw error
    }
  },

  /**
   * Get current user profile
   * @returns {Promise<{success: boolean, user?: object}>}
   */
  getProfile: async () => {
    try {
      const response = await api.get('/auth/profile')
      return response
    } catch (error) {
      throw error
    }
  },

  /**
   * Update user profile
   * @param {object} profileData - Profile data to update
   * @returns {Promise<{success: boolean, user?: object}>}
   */
  updateProfile: async (profileData) => {
    try {
      const response = await api.put('/auth/profile', profileData)
      return response
    } catch (error) {
      throw error
    }
  },

  /**
   * Change password (if applicable)
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   * @returns {Promise<{success: boolean}>}
   */
  changePassword: async (currentPassword, newPassword) => {
    try {
      const response = await api.post('/auth/change-password', {
        currentPassword,
        newPassword
      })
      return response
    } catch (error) {
      throw error
    }
  }
}

export default authApi
